import {PriceStrategyService} from "../../../components/inputs/price/service/PriceStrategyService";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {OptionContractsCache} from "../../../options/contracts/cache/OptionContractsCache";
import {Observable} from "rxjs";
import {
    ConditionalSimulatedEquityOrderViewModel,
    ConditionalSimulatedOptionOrderViewModel,
    ConditionalSimulatedTmcOrderViewModel,
    ConditionalSimulatedWarrantOrderViewModel,
    SimulatedEquityOrderViewModel,
    SimulatedOptionOrderViewModel,
    SimulatedOrderViewModel,
    SimulatedOrderViewModels,
    SimulatedTmcOrderViewModel,
    SimulatedWarrantOrderViewModel
} from "../../models/simulated/SimulatedOrderViewModel";
import {
    OptionQuoteViewModel, QuoteViewModel,
    TmcQuoteViewModel,
    UnderlyingQuoteViewModel,
    WarrantQuoteViewModel
} from "../../../quote/models/QuoteViewModel";
import {AccountBriefRecord} from "../../../account/types/AccountBriefRecord";
import {
    isSimulatedTmcOrder,
    SimulatedOrderRecord,
    SimulatedOrderRecords
} from "../../models/simulated/SimulatedOrderRecord";
import {CurrentUser} from "../../../services/user/models/CurrentUser";
import {isNotNull} from "../../../js/util/UtilFunctions";
import {isEquityOrder, isOptionOrder, isWarrantOrder, OrderState} from "../../models/OrderRecord";
import {TmcLegViewModelFactory} from "./TmcLegViewModelFactory";
import {OptionRecord} from "../../../options/contracts/models/OptionChainRecord";
import {PortfolioType} from "../../../portfolio/models/Portfolio";
import {
    isSameOrder,
    placedBrokerOrder,
    PlacedBrokerOrders,
    placedSimulatedOrder,
    PlacedSimulatedOrders
} from "../placed/models/PlacedOrder";
import {IressOrderRecord} from "../../models/iress/IressOrderRecord";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {quoteHashCode, QuoteHashMap} from "../../../quote/util/QuoteStoreUtil";
import {QuoteParameters} from "../../../quote/models/QuoteParameters";
import {isNotEmpty} from "../../models/OrderConditions";

export class SimulatedOrderViewModelFactory {

    public static readonly id = "simulatedOrderViewModelFactory";

    /** @ngInject */
    public constructor(private readonly priceStrategyService: PriceStrategyService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly optionContractsCache: OptionContractsCache,
                       private readonly tmcLegViewModelFactory: TmcLegViewModelFactory) {
    }

    public build(orders: SimulatedOrderRecords,
                 quotes: QuoteHashMap<QuoteViewModel>,
                 accounts: Map<string, AccountBriefRecord>,
                 placed: PlacedSimulatedOrders): Observable<SimulatedOrderViewModels> {
        return this.currentUserService
                   .current
                   .first()
                   .flatMap((user) => this.buildModels(user, orders, quotes, accounts, placed));
    }

    private async buildModels(user: CurrentUser,
                              orders: SimulatedOrderRecords,
                              quotes: QuoteHashMap<QuoteViewModel>,
                              accounts: Map<string, AccountBriefRecord>,
                              placed: PlacedSimulatedOrders): Promise<SimulatedOrderViewModels> {
        const orderResults = orders.map((o) => this.tryBuildModel(o, quotes, accounts, user, placed));
        const models = await Promise.all(orderResults);
        return models.filter(isNotNull);
    }

    private async tryBuildModel(order: SimulatedOrderRecord,
                                quotes: QuoteHashMap<QuoteViewModel>,
                                accounts: Map<string, AccountBriefRecord>,
                                user: CurrentUser,
                                placed: PlacedSimulatedOrders): Promise<SimulatedOrderViewModel | null> {
        try {
            const account = accounts.get(order.accountNumber) || null;
            return this.buildModel(order, quotes, account, user, placed);
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    private async buildModel(order: SimulatedOrderRecord,
                             quotes: QuoteHashMap<QuoteViewModel>,
                             account: AccountBriefRecord | null,
                             user: CurrentUser,
                             placed: PlacedSimulatedOrders): Promise<SimulatedOrderViewModel> {
        const isAmendable = (order.state === OrderState.ACTIVE) && user.ordersEnabled;
        const isMine = this.isMine(order, placed);
        const strategy = this.priceStrategyService.forCode(order.code);
        const conditions = order.conditions;
        const quote = quotes.get(order);

        if (isSimulatedTmcOrder(order)) {
            if ((quote == null) || (quote instanceof TmcQuoteViewModel)) {
                const legs = await this.tmcLegViewModelFactory.build(order, quotes, PortfolioType.SIMULATED);
                if ((conditions != null) && isNotEmpty(conditions)) {
                    return ConditionalSimulatedTmcOrderViewModel.from(order, isAmendable, quote, strategy, account, isMine, legs, conditions);
                } else {
                    return SimulatedTmcOrderViewModel.of(order, isAmendable, quote, strategy, account, isMine, legs);
                }
            } else {
                throw Error(`Expected quote of type TmcQuoteViewModel, got ${quote.constructor.name}`);
            }
        } else if (isEquityOrder(order)) {
            if ((quote == null) || (quote instanceof UnderlyingQuoteViewModel)) {
                if ((conditions != null) && isNotEmpty(conditions)) {
                    return ConditionalSimulatedEquityOrderViewModel.from(order, isAmendable, quote, strategy, account, isMine, conditions);
                } else {
                    return SimulatedEquityOrderViewModel.of(order, isAmendable, quote, strategy, account, isMine);
                }
            } else {
                throw Error(`Expected quote of type UnderlyingQuoteViewModel, got ${quote.constructor.name}`);
            }
        } else if (isOptionOrder(order)) {
            if ((quote == null) || (quote instanceof OptionQuoteViewModel)) {
                let contract: OptionRecord | null = null;
                try {
                    contract = await this.optionContractsCache.find(order);
                } catch (e) {
                    // Do nothing
                }
                if ((conditions != null) && isNotEmpty(conditions)) {
                    return ConditionalSimulatedOptionOrderViewModel.from(order, isAmendable, quote, strategy, account, isMine, contract, conditions);
                } else {
                    return SimulatedOptionOrderViewModel.of(order, isAmendable, quote, strategy, account, isMine, contract);
                }
            } else {
                throw Error(`Expected quote of type OptionQuoteViewModel, got ${quote.constructor.name}`);
            }
        } else if (isWarrantOrder(order)) {
            if ((quote == null) || (quote instanceof WarrantQuoteViewModel)) {
                if ((conditions != null) && isNotEmpty(conditions)) {
                    return ConditionalSimulatedWarrantOrderViewModel.from(order, isAmendable, quote, strategy, account, isMine, conditions);
                } else {
                    return SimulatedWarrantOrderViewModel.of(order, isAmendable, quote, strategy, account, isMine);
                }
            } else {
                throw Error(`Expected quote of type WarrantQuoteViewModel, got ${quote.constructor.name}`);
            }
        } else {
            throw Error(`Unable to create SimulatedOrderViewModel for order [${order.code}:${order.exchange}]`);
        }
    }

    private isMine(order: SimulatedOrderRecord, placedOrders: PlacedSimulatedOrders): boolean {
        const placed = placedSimulatedOrder(order.id);
        return ArrayUtil.includes(placedOrders, placed, isSameOrder);
    }
}
